import React, { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  Brain, Sparkles, BookOpen, GraduationCap, MapPin, Target,
  Heart, Star, ChevronRight, MessageSquare, Loader2, Banknote, Clock, ExternalLink,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Link } from "react-router-dom";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import BookingDialog from "../components/BookingDialog";
import { API, formatApiErrorDetail } from "../context/AuthContext";
import { useFmtXof } from "../lib/i18nFmt";

const SUBJECT_KEYS = [
  "math", "physics", "chemistry", "svt", "french", "history",
  "geo", "english", "spanish", "computing", "philo", "primary",
];
const LEVEL_KEYS = ["primary", "middle", "high", "uni"];

export default function AIMatch() {
  const { t } = useTranslation();
  const fmtXof = useFmtXof();

  const SUBJECTS = useMemo(() => SUBJECT_KEYS.map((k) => ({ key: k, label: t(`aimatch.subjects.${k}`) })), [t]);
  const LEVELS = useMemo(() => LEVEL_KEYS.map((k) => ({ key: k, label: t(`aimatch.levels.${k}`) })), [t]);

  const [form, setForm] = useState({
    subject: t("aimatch.subjects.math"),
    level: t("aimatch.levels.middle"),
    child_age: 12,
    city: "",
    learning_style: "",
    objectives: "",
    budget_max: 5000,
  });
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [criteria, setCriteria] = useState(null);
  const [error, setError] = useState("");

  // Contact dialog
  const [contactOpen, setContactOpen] = useState(false);
  const [contactEducator, setContactEducator] = useState(null);
  const [contactMessage, setContactMessage] = useState("");
  const [sending, setSending] = useState(false);

  // Booking dialog
  const [bookingOpen, setBookingOpen] = useState(false);
  const [bookingEducator, setBookingEducator] = useState(null);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const selectSubject = (s) => setForm({ ...form, subject: s });
  const selectLevel = (l) => setForm({ ...form, level: l });

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    setResults([]);
    try {
      const { data } = await axios.post(`${API}/match`, {
        ...form,
        child_age: Number(form.child_age),
        budget_max: Number(form.budget_max) || null,
      });
      setResults(data.results || []);
      setCriteria(data.criteria);
      const count = (data.results || []).length;
      if (count === 0) {
        toast.info(t("aimatch.noResults"));
      } else {
        toast.success(t("aimatch.successCount", { count }));
      }
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const openContact = (edu) => {
    setContactEducator(edu);
    setContactMessage(
      t("aimatch.contactDefault", {
        firstName: edu.name?.split(" ")[0] || "",
        subject: criteria?.subject || "...",
        level: criteria?.level || "...",
      })
    );
    setContactOpen(true);
  };

  const sendContact = async () => {
    if (!contactEducator) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact-request`, {
        educator_id: contactEducator.id,
        message: contactMessage,
      });
      toast.success(t("aimatch.contactSent", { name: contactEducator.name }));
      setContactOpen(false);
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFCFB]" data-testid="ai-match-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="rounded-3xl bg-white border border-[#E5EBE7] p-8 sm:p-10 relative overflow-hidden">
          <span className="geo-shape bg-[#16A34A]/15 h-64 w-64 -top-20 -right-20" aria-hidden />
          <div className="relative">
            <span className="pill"><Brain className="h-3.5 w-3.5" /> {t("aimatch.pill")}</span>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl font-bold text-[#0A2518]">
              {t("aimatch.title")}
            </h1>
            <p className="mt-3 text-[#4B5563] max-w-2xl">{t("aimatch.intro")}</p>
          </div>
        </div>

        {/* Form */}
        <Card className="mt-10 p-7 border-[#E5EBE7]" data-testid="ai-match-form">
          <form onSubmit={onSubmit} className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="lg:col-span-2">
              <Label className="text-[#0A2518] flex items-center gap-1.5 mb-2"><BookOpen className="h-4 w-4 text-[#16A34A]" /> {t("aimatch.subjectLabel")}</Label>
              <div className="flex flex-wrap gap-2">
                {SUBJECTS.map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => selectSubject(s.label)}
                    className={`px-4 py-2 rounded-full text-sm border transition-all ${
                      form.subject === s.label
                        ? "bg-[#16A34A] text-white border-[#16A34A]"
                        : "bg-white text-[#0A2518] border-[#E5EBE7] hover:border-[#16A34A]/60"
                    }`}
                    data-testid={`ai-subject-${s.key}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-[#0A2518] flex items-center gap-1.5 mb-2"><GraduationCap className="h-4 w-4 text-[#16A34A]" /> {t("aimatch.levelLabel")}</Label>
              <div className="flex flex-wrap gap-2">
                {LEVELS.map((l) => (
                  <button
                    key={l.key}
                    type="button"
                    onClick={() => selectLevel(l.label)}
                    className={`px-4 py-2 rounded-full text-sm border transition-all ${
                      form.level === l.label
                        ? "bg-[#16A34A] text-white border-[#16A34A]"
                        : "bg-white text-[#0A2518] border-[#E5EBE7] hover:border-[#16A34A]/60"
                    }`}
                    data-testid={`ai-level-${l.key}`}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-[#0A2518] flex items-center gap-1.5"><Heart className="h-4 w-4 text-[#16A34A]" /> {t("aimatch.childAge")}</Label>
              <Input name="child_age" type="number" min={4} max={25} value={form.child_age} onChange={onChange} className="mt-1.5 h-11" data-testid="ai-age-input" />
            </div>

            <div>
              <Label className="text-[#0A2518] flex items-center gap-1.5"><MapPin className="h-4 w-4 text-[#16A34A]" /> {t("aimatch.city")}</Label>
              <Input name="city" value={form.city} onChange={onChange} className="mt-1.5 h-11" placeholder={t("aimatch.cityPh")} data-testid="ai-city-input" />
            </div>

            <div>
              <Label className="text-[#0A2518] flex items-center gap-1.5"><Banknote className="h-4 w-4 text-[#16A34A]" /> {t("aimatch.budget")}</Label>
              <Input name="budget_max" type="number" min={500} step="500" value={form.budget_max} onChange={onChange} className="mt-1.5 h-11" data-testid="ai-budget-input" />
            </div>

            <div className="lg:col-span-2">
              <Label className="text-[#0A2518] flex items-center gap-1.5"><Target className="h-4 w-4 text-[#16A34A]" /> {t("aimatch.styleLabel")}</Label>
              <Input name="learning_style" value={form.learning_style} onChange={onChange} className="mt-1.5 h-11" placeholder={t("aimatch.stylePh")} data-testid="ai-style-input" />
            </div>

            <div className="lg:col-span-2">
              <Label className="text-[#0A2518]">{t("aimatch.objectives")}</Label>
              <Textarea name="objectives" value={form.objectives} onChange={onChange} className="mt-1.5 min-h-[90px]" placeholder={t("aimatch.objectivesPh")} data-testid="ai-objectives-input" />
            </div>

            {error && (
              <div className="lg:col-span-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>
            )}

            <div className="lg:col-span-2">
              <Button type="submit" disabled={loading} className="rounded-full h-14 px-8 bg-[#16A34A] hover:bg-[#15803D] text-white text-base" data-testid="ai-submit-btn">
                {loading ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t("aimatch.thinking")}</>) : (<><Sparkles className="h-4 w-4 mr-2" />{t("aimatch.submit")}</>)}
              </Button>
            </div>
          </form>
        </Card>

        {/* Results */}
        {results.length > 0 && (
          <div className="mt-12" data-testid="ai-results">
            <h2 className="font-display text-3xl font-bold text-[#0A2518] mb-2">
              {t("aimatch.resultsTitle", { count: results.length })}
            </h2>
            <p className="text-[#4B5563] mb-6">{t("aimatch.resultsIntro")}</p>

            <div className="grid md:grid-cols-2 gap-6">
              {results.map(({ educator, score, reason }) => {
                const ep = educator.profile || {};
                return (
                  <Card key={educator.id} className="p-7 border-[#E5EBE7] ai-glow rounded-2xl relative" data-testid={`ai-result-${educator.id}`}>
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div>
                        <Link to={`/educators-list/${educator.id}`} className="hover:text-[#15803D] transition-colors" data-testid={`ai-profile-${educator.id}`}>
                          <h3 className="font-display text-2xl font-semibold text-[#0A2518] hover:text-[#15803D] inline-flex items-center gap-1">
                            {educator.name}
                            <ExternalLink className="h-4 w-4 opacity-60" />
                          </h3>
                        </Link>
                        <div className="mt-1 flex items-center gap-3 text-sm text-[#4B5563]">
                          {ep.city && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-[#16A34A]" />{ep.city}</span>}
                          <span className="flex items-center gap-1"><Star className="h-3.5 w-3.5 text-yellow-500 fill-yellow-500" />{(ep.rating ?? 5).toFixed(1)}</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-[#16A34A] text-white font-display font-bold">
                          {Math.round(score)}
                        </div>
                        <div className="text-[10px] text-[#4B5563] mt-1 uppercase tracking-wide">Match</div>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {(ep.subjects || []).slice(0, 4).map((s) => (
                        <Badge key={s} className="bg-[#F0FDF4] text-[#15803D] border border-[#16A34A]/30">{s}</Badge>
                      ))}
                    </div>

                    <p className="text-sm text-[#4B5563] leading-relaxed">{ep.bio}</p>

                    <div className="mt-4 p-3 rounded-xl bg-[#F0FDF4] border border-[#16A34A]/15">
                      <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-[#15803D] mb-1">
                        <Sparkles className="h-3.5 w-3.5" /> {t("aimatch.why")}
                      </div>
                      <p className="text-sm text-[#0A2518]">{reason}</p>
                    </div>

                    <div className="mt-5 flex items-center justify-between flex-wrap gap-3 text-sm text-[#4B5563]">
                      <span className="flex items-center gap-1"><Banknote className="h-3.5 w-3.5 text-[#16A34A]" />{fmtXof(ep.hourly_rate)}/h</span>
                      <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5 text-[#16A34A]" />{t("aimatch.experience", { years: ep.experience_years || 0 })}</span>
                    </div>

                    <Button onClick={() => openContact(educator)} variant="outline" className="mt-5 rounded-full border-[#16A34A] text-[#16A34A] hover:bg-[#F0FDF4] w-full" data-testid={`ai-contact-${educator.id}`}>
                      <MessageSquare className="h-4 w-4 mr-2" />
                      {t("aimatch.contact")}
                    </Button>
                    <Button
                      onClick={() => { setBookingEducator(educator); setBookingOpen(true); }}
                      className="mt-2 rounded-full bg-[#16A34A] hover:bg-[#15803D] text-white w-full"
                      data-testid={`ai-book-${educator.id}`}
                    >
                      {t("aimatch.book")}
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </Card>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Contact dialog */}
      <Dialog open={contactOpen} onOpenChange={setContactOpen}>
        <DialogContent className="bg-white" data-testid="ai-contact-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#0A2518]">
              {t("aimatch.contactDialogTitle", { name: contactEducator?.name || "" })}
            </DialogTitle>
            <DialogDescription>
              {t("aimatch.contactDialogDesc")}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={contactMessage}
            onChange={(e) => setContactMessage(e.target.value)}
            className="min-h-[160px]"
            data-testid="ai-contact-message"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setContactOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={sendContact} disabled={sending || contactMessage.trim().length < 5} className="bg-[#16A34A] hover:bg-[#15803D] text-white rounded-full" data-testid="ai-contact-send-btn">
              {sending ? t("aimatch.sending") : t("aimatch.send")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Booking + Payment dialog */}
      <BookingDialog
        open={bookingOpen}
        onOpenChange={setBookingOpen}
        educator={bookingEducator}
        defaultSubject={criteria?.subject}
      />
    </div>
  );
}
